import {
  RegistrationData,
  RegistrationFormState,
  SubmitError,
  SubmitResponse,
  ValidationErrors,
} from './registrationForm.types';
import { pick } from 'lodash-es';
import { getValidationErrors, mapFormData } from './registrationForm.utils';
import { DefaultRegistrationData } from './registrationForm.constants';
import { RegistrationConfig } from '../registrationConfig/registrationConfig.types';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import {
  persistReducer,
  injectSlice,
  State,
  createAsyncThunk,
  createSlice,
  createSelector,
  PayloadAction,
  selectMarketingMeta,
  resetMarketingMeta,
  selectMarketingMetaHistory,
} from '@bonanza-social/toolkit/state';
import {
  selectRegistrationConfig,
  selectRegistrationConfigVariant,
} from '../registrationConfig/registrationConfig.slice';
import {
  selectRegistrationOffers,
  selectRegistrationOffersLength,
} from '../registrationOffers/registrationOffers.slice';
import RegistrationService from '../../services/RegistrationService/RegistrationService';
import { ApiError } from '@bonanza-social/toolkit/api';
import pkceChallenge from 'react-native-pkce-challenge';
import { RegistrationVariantEnum } from '../../utils/fetchRegistrationVariant';

export const submitRegistrationForm = createAsyncThunk<
  SubmitResponse,
  RegistrationData,
  { rejectValue: SubmitError | ApiError }
>(
  'registrationForm/submit',
  async (formData, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState() as State;
      const marketingMeta = selectMarketingMeta(state);
      const marketingMetaHistory = selectMarketingMetaHistory(state);

      const { codeChallenge, codeVerifier } = pkceChallenge();
      const variant =
        selectRegistrationConfigVariant(state) ===
        RegistrationVariantEnum.threeStep
          ? RegistrationVariantEnum.threeStep
          : RegistrationVariantEnum.elevenStep;

      const formDataWithVariant: RegistrationData = {
        ...formData,
        registration_variant: variant,
      };

      const mappedData = mapFormData(
        formDataWithVariant,
        marketingMeta,
        marketingMetaHistory,
        codeChallenge,
      );

      const response = await RegistrationService.register({
        data: mappedData,
        headers: {
          'X-Fit-To-Play': formData.fit,
        },
      });

      const { status } = response;

      if (status === 'Suspended') {
        return rejectWithValue({ errorCode: 'account_suspended' });
      }

      dispatch(resetRegistrationData());
      dispatch(resetMarketingMeta());
      const accountData = { ...response, codeVerifier };
      return { account: accountData, formData } as SubmitResponse;
    } catch (error) {
      if (error instanceof ApiError) {
        return rejectWithValue(error);
      }

      throw error;
    }
  },
);

export const resetRegistrationData = createAsyncThunk(
  'registrationForm/reset',
  (_, { getState }) => {
    const state = getState() as State;
    const data = selectRegistrationFormData(state);
    const offersLength = selectRegistrationOffersLength(state);
    const marketingMeta = selectMarketingMeta(state);

    const newData = { ...DefaultRegistrationData };
    const { referral_code, utm_campaign } = marketingMeta;

    if (referral_code || utm_campaign) {
      newData.promoId = data.promoId;
    } else if (offersLength > 0) {
      newData.promoId = -1;
    }

    return newData;
  },
);

const initialState: RegistrationFormState = {
  data: { ...DefaultRegistrationData },
  dataState: 'idle',
  autoLoginState: 'idle',
  validationErrors: getValidationErrors(DefaultRegistrationData),
  stepIndex: 0,
  returnToSummary: false,
  preventRemove: true,
};

const registrationForm = createSlice({
  name: 'registrationForm',
  initialState,
  reducers: {
    updateRegistrationData(
      state,
      action: PayloadAction<Partial<RegistrationData>>,
    ) {
      state.data = {
        ...state.data,
        ...action.payload,
      };
    },
    setValidationErrors(state, action: PayloadAction<ValidationErrors>) {
      state.validationErrors = action.payload;
    },
    setFormDataConfigId(
      state,
      action: PayloadAction<RegistrationConfig['id']>,
    ) {
      state.configId = action.payload;
    },
    setStepIndex(state, action: PayloadAction<number>) {
      state.stepIndex = action.payload;
    },
    setReturnToSummary(state, action: PayloadAction<boolean>) {
      state.returnToSummary = action.payload;
    },
    setMarketingOptIn(state, action: PayloadAction<boolean>) {
      state.data.marketing = action.payload;
    },
    setAutoLoginPending(state) {
      state.autoLoginState = 'pending';
    },
    setAutoLoginFulfilled(state) {
      state.autoLoginState = 'fulfilled';
    },
    setAutoLoginRejected(state) {
      state.autoLoginState = 'rejected';
    },
    setPreventRemove(state, action: PayloadAction<boolean>) {
      state.preventRemove = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(submitRegistrationForm.pending, (state) => {
      state.dataState = 'pending';
    });
    builder.addCase(submitRegistrationForm.fulfilled, (state) => {
      state.dataState = 'fulfilled';
    });
    builder.addCase(submitRegistrationForm.rejected, (state, action) => {
      state.dataState = 'rejected';
      if (action.payload && 'errorCode' in action.payload) {
        state.errorCode = action.payload.errorCode;
      } else {
        state.errorCode = undefined;
      }
    });
    builder.addCase(resetRegistrationData.fulfilled, (state, action) => {
      state.data = action.payload;
    });
  },
});

export const {
  updateRegistrationData,
  setValidationErrors,
  setFormDataConfigId,
  setStepIndex,
  setReturnToSummary,
  setAutoLoginPending,
  setAutoLoginFulfilled,
  setAutoLoginRejected,
  setPreventRemove,
  setMarketingOptIn,
} = registrationForm.actions;

const persistConfig = {
  key: 'registrationForm',
  storage: AsyncStorage,
  whitelist: ['data', 'validationErrors', 'configId'],
  version: 1,
};

const persistedReducer = persistReducer(
  persistConfig,
  registrationForm.reducer,
);

const withRegistrationForm = injectSlice({
  reducerPath: 'registrationForm',
  reducer: persistedReducer,
});

export const selectRegistrationStepIndex = withRegistrationForm.selector(
  (state) => state.registrationForm.stepIndex,
);

export const selectRegistrationProgress = createSelector(
  [selectRegistrationStepIndex, selectRegistrationConfig],
  (stepIndex, config) => {
    const { startPercentage = 0, endPercentage = 100 } = config;
    const progressRange = endPercentage - startPercentage;

    const currentProgress =
      startPercentage + (stepIndex / (config.steps.length - 1)) * progressRange;

    return Math.ceil(currentProgress / 5) * 5;
  },
);

export const selectRegistrationFormConfigId = withRegistrationForm.selector(
  (state) => state.registrationForm.configId,
);

export const selectRegistrationFormData = withRegistrationForm.selector(
  (state) => state.registrationForm.data,
);

export const selectRegistrationFormPromoId = withRegistrationForm.selector(
  (state) => state.registrationForm.data.promoId,
);

export const selectRegistrationFormValidationErrors =
  withRegistrationForm.selector(
    (state) => state.registrationForm.validationErrors,
  );

export const selectRegistrationAutoLoginState = withRegistrationForm.selector(
  (state) => state.registrationForm.autoLoginState,
);

export const selectPreventRemove = withRegistrationForm.selector(
  (state) => state.registrationForm.preventRemove,
);

export const selectFormErrorsByFields = (fields: string[]) =>
  createSelector(selectRegistrationFormValidationErrors, (errors) =>
    pick(errors, fields),
  );

export const selectFirstErroredStepIndex = withRegistrationForm.selector(
  createSelector(
    [
      selectRegistrationOffers,
      selectRegistrationFormData,
      selectRegistrationConfig,
      (state: State) => state,
    ],
    (offers, formData, config, state) => {
      const referralPromo = offers.find(
        (offer) =>
          offer.promoType === 'Referral' || offer.subPromoType === 'Referral',
      );

      if (referralPromo && !formData.firstStep) {
        return 0;
      }

      if (offers && offers.length > 0 && !formData.promoId) {
        const promoStepIndex = config.steps.findIndex((step) =>
          step.fields?.includes('promoId'),
        );

        if (promoStepIndex !== -1) {
          return promoStepIndex;
        }
      }

      const firstErroredStepIndex = config.steps.findIndex((step) => {
        if (!step.fields?.length) return false;

        const errors = selectFormErrorsByFields(step.fields)(state);

        return Object.keys(errors).length > 0;
      });

      if (firstErroredStepIndex !== -1) {
        return firstErroredStepIndex;
      }

      return config.steps.length - 1;
    },
  ),
);

export const selectRegistrationLimits = withRegistrationForm.selector(
  (state) => state.registrationForm.data.limits,
);

export const selectShouldReturnToSummary = withRegistrationForm.selector(
  (state) => state.registrationForm.returnToSummary,
);

export const selectRegistrationFormDataState = withRegistrationForm.selector(
  (state) => state.registrationForm.dataState,
);

export const selectRegistrationFormSubmitError = withRegistrationForm.selector(
  (state) => state.registrationForm.errorCode,
);

declare global {
  interface LazyLoadedSlices {
    registrationForm: ReturnType<typeof persistedReducer>;
  }
}
